
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { TerraceFormValues } from "./SubmissionFormSchema";

import TerraceNameAndType from './form-fields/TerraceNameAndType';
import TerraceAddress from './form-fields/TerraceAddress';
import TerraceDescription from './form-fields/TerraceDescription';
import TerraceContact from './form-fields/TerraceContact';
import TerraceFeatures from './form-fields/TerraceFeatures';
import TerracePhotoManager from './form-fields/TerracePhotoManager';
import OpeningHoursPerDay from '../../terrace-submission/OpeningHoursPerDay';
import CoordinatesDisplay from '../terraces/CoordinatesDisplay';
import { TerraceSubmission } from '@/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';

interface SubmissionFormProps {
  form: UseFormReturn<TerraceFormValues>;
  submissionId?: string;
  googlePlaceId?: string;
  placeName?: string;
  submission?: TerraceSubmission;
}

const SubmissionForm = ({ form, submissionId, googlePlaceId, placeName, submission }: SubmissionFormProps) => {
  const watchedValues = form.watch();

  // Prefer googleMapsUrl from the form, then submission.original_maps_url as fallback
  const googleMapsUrl =
    watchedValues.googleMapsUrl ||
    submission?.original_maps_url ||
    "";

  return (
    <div className="space-y-6">
      <TerraceNameAndType form={form} />
      <div className="space-y-4">
        <TerraceAddress form={form} />

        {/* Show the Google Maps URL field after the address */}
        <div className="flex items-end gap-2 pl-4">
          <div className="flex-1">
            <Label htmlFor="google-maps-url" className="block text-xs text-muted-foreground mb-1">
              Google Maps URL
            </Label>
            <Input
              id="google-maps-url"
              type="text"
              className="text-xs font-mono bg-muted w-full"
              value={googleMapsUrl}
              readOnly
              tabIndex={-1}
            />
          </div>
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in Google Maps"
            >
              <Button size="icon" variant="outline" className="mb-2 p-2" type="button">
                <ExternalLink size={16} className="text-blue-500" />
              </Button>
            </a>
          )}
        </div>

        {/* Display coordinates under the address field */}
        {watchedValues.lat && watchedValues.lng && (
          <div className="pl-4 border-l-2 border-sky-mist/30">
            <div className="text-sm text-muted-foreground mb-2">Location Coordinates</div>
            <CoordinatesDisplay 
              lat={watchedValues.lat} 
              lng={watchedValues.lng} 
              className="mb-2"
            />
          </div>
        )}
      </div>

      <TerracePhotoManager 
        form={form} 
        submissionId={submissionId}
        googlePlaceId={googlePlaceId}
        placeName={placeName}
      />

      <OpeningHoursPerDay form={form} />

      <TerraceDescription form={form} />

      <TerraceContact form={form} />

      <TerraceFeatures form={form} submission={submission} />
    </div>
  );
};

export default SubmissionForm;
